import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    CategoryForm,
    FieldErrorList,
    FormField,
    IndicatorCountryList,
    IndicatorDetails,
    Input,
    Label,
    MessageModal,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    RadioButton,
    TabNavigation,
    TextArea
} from 'common/components';
import {
    ApiStatus,
    ButtonLabel,
    ButtonVariant,
    CategoryField,
    InputType,
    MessageType
} from 'common/enums';
import {
    INDICATOR_TAB_VALUES,
    COPY_CONFIRMATION_TITLE,
    COPY_CONFIRMATION_DESCRIPTION,
    ACTION_ITEMS,
    COPY_VALIDATION_TITLE,
    COPY_VALIDATION_ERROR_MESSAGE,
    INDICATOR_NAME_DUPLICATE_ERROR,
    INDICATOR_SOURCE_REQUIRED,
    INDICATOR_WEB_ADDRESS_REQUIRED
} from 'common/config';
import SubcategoryForm from 'common/components/custom/sub-category-form/SubcategoryForm';
import { removeTimeOffset } from 'common/utils/date';
import { addValidationError, removeValidationError } from 'common/utils/error';
import { MasterDataEntity, Source } from 'common/models/data/entity.model';
import { EntityType, IndicatorDetailFields, SubCategoryField } from 'common/enums/data.enum';
import { IndicatorAddDTO, IndicatorLocationTemp } from 'common/models/data/indicator.model';
import { hasNoValue, hasValue, updateIndicatorModel } from 'common/utils/data';
import { entityActions } from 'common/redux/entity';
import { RootState } from 'common/redux/core/root.reducer';
import { getIndicatorDetailsForEdit, getSourceList } from 'common/redux/entity/entity.selector';
import {
    validateAndUpdateIndicatorModel,
    validateAndUpdateIndicatorValue,
    validateCategoryDescription,
    validateCategoryForm,
    validateCategoryName,
    validateCategorySortOrder,
    validateIndicatorDetailsForm,
    validateSubCategoryDescription,
    validateSubCategoryForm,
    validateSubCategoryName,
    validateSubCategorySortOrder
} from 'common/utils/validator';

import SingleSelectMasterData from 'modules/master-data/components/single-select-master-data/SingleSelectMasterData';
import { IndicatorCopyTitles, NestCreationTypes } from 'modules/master-data/enums/mode.enum';
import { ADD_NEW_SUB_CATEGORY_BANNER_MESSAGE } from 'modules/configuration/config';
import {
    getDuplicateIndicatorResponse,
    getFieldErrors
} from 'modules/master-data/redux/masterData.selector';
import { masterDataActions } from 'modules/master-data/redux';

import './IndicatorCopy.scss';
import { FieldError } from 'common/models';
import useIndicatorSourceAccess from 'modules/configuration/roles-access-hooks/useIndicatorSourceAccess';

type IndicatorCopyProps = {
    isVisible: boolean;
    data: any;
    hierarchicalData: MasterDataEntity[];
    primaryButtonDisable: boolean;
    onClose: (resetData?: boolean) => void;
    fetchNextHierachySet: () => void;
};

export default function IndicatorCopy({
    isVisible,
    data,
    hierarchicalData,
    primaryButtonDisable,
    onClose,
    fetchNextHierachySet
}: IndicatorCopyProps) {
    const [tab, setTab] = useState<string>(INDICATOR_TAB_VALUES[0]);
    const [indicatorModel, setIndicatorModel] = useState<any>({
        description: data.description,
        id: data.id,
        name: data.name,
        order: data.order,
        reference: data.reference,
        sourceId: data.sourceId,
        frequencyTypeId: data.frequencyTypeId,
        valueType: data.valueType,
        webAddress: data.webAddress,
        sourceDate: data.sourceDate,
        sortOrder: data.sortOrder,
        countryData: data.countryData,
        dataCount: data.dataCount
    });
    const [isOverride, setOverride] = useState<boolean>(false);
    const [isShowUpdateTypeAlert, setShowUpdateTypeAlert] = useState<boolean>(false);
    const [dataCount, setDataCount] = useState<number>(0);
    const [indicatorCountryData, setIndicatorCountryData] = useState<IndicatorLocationTemp[]>();
    const [targetId, setTargetId] = useState<string>('');
    const [isConfirmationVisible, setIsConfirmationVisible] = useState<boolean>(false);
    const [isValidationPopupVisible, setIsValidationPopupVisible] = useState<boolean>(false);
    const [wizardScreen, setWizardScreen] = useState<number>(0);
    const [createNewEntityType, setCreateNewEntityType] = useState<string>('');
    const [validationPopupDetails, setValidationPopupDetails] = useState({
        title: '',
        text: '',
        type: ''
    });
    const [header, setHeader] = useState(IndicatorCopyTitles.MAIN);
    const [name, setName] = useState('');
    const [sortOrder, setSortOrder] = useState('');
    const [description, setDescription] = useState('');
    const [targetModelData, setTargetModelData] = useState<any>();
    const [subCatName, setSubCatName] = useState('');
    const [subCatSortOrder, setSubCatSortOrder] = useState('');
    const [subCatDescription, setSubCatDescription] = useState('');
    const [sourceSearchValue, setSourceSearchValue] = useState<string>('');

    const indicator = useSelector((state: RootState) => getIndicatorDetailsForEdit(state));
    const sourceList = useSelector((state: RootState) => getSourceList(state));
    const fieldErrors = useSelector((state: RootState) => getFieldErrors(state));
    const isDuplicateIndicator = useSelector((state: RootState) =>
        getDuplicateIndicatorResponse(state)
    );

    const { isSetUpDisabled } = useIndicatorSourceAccess();

    const dispatch = useDispatch();

    const handleSave = useCallback(() => {
        setIsConfirmationVisible(true);
        onClose();
    }, [onClose]);

    const handlePrimaryAction = useCallback(() => {
        if (createNewEntityType || targetModelData?.id) {
            if (createNewEntityType == NestCreationTypes.CATEGORY) {
                if (wizardScreen === 0) {
                    setHeader(IndicatorCopyTitles.CREATE_CATEGORY);
                    setWizardScreen(1);
                } else if (wizardScreen == 1) {
                    let errors = fieldErrors;
                    errors = validateCategoryForm(name, sortOrder, errors);
                    if (errors.length === 0) {
                        setHeader(IndicatorCopyTitles.CREATE_SUB_CATEGORY);
                        setWizardScreen(2);
                    }
                    dispatch(masterDataActions.setFieldErrors(errors));
                } else {
                    let errors = fieldErrors;
                    errors = validateSubCategoryForm(subCatName, subCatSortOrder, errors);
                    if (errors.length === 0) {
                        handleSave();
                    }
                    dispatch(masterDataActions.setFieldErrors(errors));
                }
            } else if (
                createNewEntityType == NestCreationTypes.SUB_CATEGORY &&
                wizardScreen === 0
            ) {
                setHeader(IndicatorCopyTitles.CREATE_SUB_CATEGORY);
                setWizardScreen(2);
                dispatch(masterDataActions.deselectRadioButton());
                dispatch(masterDataActions.collapseHierarchicalData());
            } else if (
                createNewEntityType == NestCreationTypes.SUB_CATEGORY &&
                wizardScreen === 2
            ) {
                if (!targetModelData) {
                    setValidationPopupDetails({
                        title: COPY_VALIDATION_TITLE,
                        text: COPY_VALIDATION_ERROR_MESSAGE,
                        type: ''
                    });
                    setIsValidationPopupVisible(true);
                } else {
                    let errors = fieldErrors;
                    errors = validateSubCategoryForm(subCatName, subCatSortOrder, errors);
                    if (errors.length === 0) {
                        handleSave();
                    }
                    dispatch(masterDataActions.setFieldErrors(errors));
                }
            } else {
                handleSave();
            }
        } else {
            handleSave();
        }
    }, [
        createNewEntityType,
        dispatch,
        fieldErrors,
        handleSave,
        name,
        sortOrder,
        subCatName,
        subCatSortOrder,
        targetModelData,
        wizardScreen
    ]);

    useEffect(() => {
        isVisible &&
            !indicator?.data?.details &&
            dispatch(entityActions.fetchSources(data.initiator));
    }, [data, data.initiator, dispatch, indicator?.data?.details, isVisible]);

    useEffect(() => {
        isVisible &&
            !indicator?.data?.details &&
            dispatch(entityActions.fetchIndicatorDetailsForEdit(data));
    }, [data, dispatch, indicator?.data?.details, isVisible]);

    useEffect(() => {
        const updatedIndicatorModel = updateIndicatorModel(indicator);
        setIndicatorModel(updatedIndicatorModel);

        if (sourceList?.data) {
            const sourceName = sourceList?.data?.find(
                (item: any) => item.sourceId === updatedIndicatorModel.sourceId
            )?.sourceName;
            setSourceSearchValue(sourceName || '');
        }

        const indicatorCountryList: IndicatorLocationTemp[] = [];
        if (indicator?.data?.locations) {
            indicator?.data.locations.forEach((loc) => {
                indicatorCountryList.push({
                    locationId: loc.locationId,
                    locationName: loc.locationName,
                    value: loc.value,
                    tempValue: loc.value,
                    indicatorId: data.id,
                    locationDetailsId: loc.locationDetailsId
                });
            });
            setDataCount(
                indicator?.data.locations.filter(
                    (loc) => !hasNoValue(loc.value) || loc.value?.trim().length > 0
                ).length
            );
        }
        setIndicatorCountryData(indicatorCountryList);
    }, [data.id, indicator, sourceList?.data]);

    useEffect(() => {
        !targetId && setTargetId(data.parentHierarchyId);
    }, [data.parentHierarchyId, targetId]);

    useEffect(() => {
        if (isDuplicateIndicator.status === ApiStatus.SUCCESS) {
            let errors = removeValidationError(fieldErrors, IndicatorDetailFields.Name);
            if (isDuplicateIndicator.data) {
                errors = addValidationError(
                    errors,
                    IndicatorDetailFields.Name,
                    INDICATOR_NAME_DUPLICATE_ERROR
                );
            }
            if (errors.length === 0) {
                handlePrimaryAction();
            }
            dispatch(masterDataActions.setFieldErrors(errors));
            dispatch(masterDataActions.resetIsDuplicateIndicator());
        } else if (isDuplicateIndicator.status === ApiStatus.ERROR) {
            dispatch(masterDataActions.resetIsDuplicateIndicator());
        }
    }, [
        createNewEntityType,
        dispatch,
        fieldErrors,
        handlePrimaryAction,
        handleSave,
        isDuplicateIndicator,
        name,
        sortOrder,
        subCatName,
        subCatSortOrder,
        targetModelData,
        wizardScreen
    ]);

    const handleChange = (field: string, value: any) => {
        const errors = removeValidationError(fieldErrors, field);
        const validationDetails = validateAndUpdateIndicatorModel(
            field,
            value,
            errors,
            sourceList,
            indicatorModel
        );
        setIndicatorModel(validationDetails.data);
        dispatch(masterDataActions.setFieldErrors(validationDetails.errors));
    };

    const handleTabClick = (tab: string) => {
        setTab(tab);
    };

    const handleIndicatorValueChange = (countryName: string, value: string) => {
        const errors = removeValidationError(fieldErrors, countryName);
        const validationDetails = validateAndUpdateIndicatorValue(
            countryName,
            value,
            errors,
            indicatorCountryData,
            indicatorModel
        );
        setIndicatorCountryData(validationDetails.data);
        dispatch(masterDataActions.setFieldErrors(validationDetails.errors));
    };

    const validateCopyForm = () => {
        let errors = fieldErrors;
        errors = validateIndicatorDetailsForm(indicatorModel, data, errors);
        dispatch(masterDataActions.setFieldErrors(errors));
        indicatorModel.name.trim() &&
            dispatch(masterDataActions.checkDuplicateIndicator(indicatorModel.name.trim()));
        return errors;
    };

    const handleRadioButtonChange = (data: any) => {
        dispatch(
            masterDataActions.setRadioButtonSelected({
                targetId: data.id
            })
        );
        setTargetId(data.hierarchyId);
    };

    const handleConfirmationClose = () => {
        setIsConfirmationVisible(false);
        onClose(true);
    };

    const handleConfirmation = () => {
        const updatedData: IndicatorAddDTO = {
            ...data,
            ...indicatorModel,
            valueType: hasValue(indicatorModel.valueType) ? indicatorModel.valueType : '',
            indicatorData: indicatorCountryData,
            activeStatus: 1,
            sourceDate: indicatorModel.sourceDate
                ? removeTimeOffset(indicatorModel.sourceDate)
                : indicatorModel.sourceDate,
            hierarchyId: targetId,
            createNewEntityType: createNewEntityType
        };
        if (!createNewEntityType) {
            dispatch(masterDataActions.copyIndicator(updatedData));
        } else if (createNewEntityType === NestCreationTypes.CATEGORY) {
            dispatch(
                masterDataActions.copyIndicator({
                    ...updatedData,
                    addSubCategoryDto: {
                        subCategoryName: subCatName,
                        order: subCatSortOrder,
                        description: subCatDescription,
                        parentSubCategoryId: null,
                        parentHierarchyId: null,
                        categoryId: null,
                        subCategoryLevel: 1
                    },
                    addCategoryDto: {
                        categoryName: name,
                        categoryOrder: sortOrder,
                        description: description
                    }
                })
            );
        } else {
            dispatch(
                masterDataActions.copyIndicator({
                    ...updatedData,
                    addSubCategoryDto: {
                        subCategoryName: subCatName,
                        order: subCatSortOrder,
                        description: subCatDescription,
                        parentSubCategoryId:
                            targetModelData.entityType == EntityType.SubCategory
                                ? targetModelData.id
                                : null,
                        parentHierarchyId: targetModelData.hierarchyId,
                        categoryId: targetModelData.catId,
                        subCategoryLevel: targetModelData.subCategoryLevel
                            ? parseInt(targetModelData.subCategoryLevel) + 1
                            : 1
                    }
                })
            );
        }
        setCreateNewEntityType('');
        handleConfirmationClose();
    };

    const handleCategoryDescription = (event: any) => {
        let errors = removeValidationError(fieldErrors, CategoryField.Description);
        errors = validateCategoryDescription(event.target.value, errors);
        setDescription(event.target.value);
        dispatch(masterDataActions.setFieldErrors(errors));
    };

    const handleCategoryNameChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, CategoryField.Name);
        errors = validateCategoryName(event.target.value, errors);
        setName(event.target.value);
        dispatch(masterDataActions.setFieldErrors(errors));
    };

    const handleCategorySortOrderChange = (value: string) => {
        let errors = removeValidationError(fieldErrors, CategoryField.SortOrder);
        errors = validateCategorySortOrder(value, errors);
        dispatch(masterDataActions.setFieldErrors(errors));
        setSortOrder(value);
    };

    const handleSubCatDescription = (event: any) => {
        let errors = removeValidationError(fieldErrors, SubCategoryField.Description);
        errors = validateSubCategoryDescription(event.target.value, errors);
        setSubCatDescription(event.target.value);
        dispatch(masterDataActions.setFieldErrors(errors));
    };

    const handleSubCatNameChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, SubCategoryField.Name);
        errors = validateSubCategoryName(event.target.value, errors);
        setSubCatName(event.target.value);
        dispatch(masterDataActions.setFieldErrors(errors));
    };

    const handleSubCatSortOrderChange = (value: string) => {
        let errors = removeValidationError(fieldErrors, SubCategoryField.SortOrder);
        errors = validateSubCategorySortOrder(value, errors);
        dispatch(masterDataActions.setFieldErrors(errors));
        setSubCatSortOrder(value);
    };

    const onClickOfRadioButton = (radioButtonValue: string) => {
        setCreateNewEntityType(radioButtonValue);
        dispatch(masterDataActions.deselectRadioButton());
    };

    const onBackButtonClicked = () => {
        if (wizardScreen == 2) {
            if (createNewEntityType === NestCreationTypes.SUB_CATEGORY) {
                setHeader(IndicatorCopyTitles.MAIN);
                setWizardScreen(0);
            } else {
                setHeader(IndicatorCopyTitles.CREATE_CATEGORY);
                setWizardScreen(1);
            }
        } else if (wizardScreen == 1) {
            setHeader(IndicatorCopyTitles.MAIN);
            setWizardScreen(0);
        }
        dispatch(masterDataActions.setFieldErrors([]));
    };

    const handleClose = () => {
        setName('');
        setSortOrder('');
        setDescription('');
        setSubCatName('');
        setSubCatSortOrder('');
        setSubCatDescription('');
        setWizardScreen(0);
        setHeader(IndicatorCopyTitles.MAIN);
        setTargetModelData(null);
        setCreateNewEntityType('');
        onClose(true);
    };

    const handleRadioClicked = (data: any) => {
        dispatch(
            masterDataActions.setRadioButtonSelected({
                targetId: data.id
            })
        );
        setTargetModelData(data);
    };

    const handleExpansionToggle = (rowData: any) => {
        dispatch(masterDataActions.toggleHierarchicalDataExpansion(rowData));
    };

    const handleSourceSearchChange = (value: string) => {
        setSourceSearchValue(value);
        let errors = removeValidationError(fieldErrors, IndicatorDetailFields.SourceId);
        if (value.trim().length === 0) {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.SourceId,
                INDICATOR_SOURCE_REQUIRED
            );
            errors = addValidationError(
                errors,
                IndicatorDetailFields.WebAddress,
                INDICATOR_WEB_ADDRESS_REQUIRED
            );
            setIndicatorModel({ ...indicatorModel, [IndicatorDetailFields.SourceId]: '' });
        }
        dispatch(masterDataActions.setFieldErrors(errors));
    };

    const handleSourceSelection = (value: string) => {
        setSourceSearchValue(value);
        if (value.trim().length > 0) {
            const sourceError = removeValidationError(fieldErrors, IndicatorDetailFields.SourceId);
            dispatch(masterDataActions.setFieldErrors(sourceError));
        }
    };

    const updateFieldErrors = (errors: Array<FieldError>) => {
        dispatch(masterDataActions.setFieldErrors(errors));
    };

    const isDataLoading =
        indicator?.status === ApiStatus.LOADING || sourceList?.status === ApiStatus.LOADING;

    return (
        <>
            <Modal
                show={isVisible && !isDataLoading}
                onClose={handleClose}
                style={{
                    width:
                        wizardScreen === 0
                            ? '85vw'
                            : wizardScreen === 2 &&
                              createNewEntityType === NestCreationTypes.SUB_CATEGORY
                            ? '70vw'
                            : '51vw',
                    height: '84vh'
                }}
                className="indicator-copy-modal">
                <ModalHeader className="indicator-copy-header">{header}</ModalHeader>
                <ModalBody className="indicator-copy-body">
                    {wizardScreen == 0 ? (
                        <>
                            <div className="indicator-copy-container">
                                <div className="indicator-copy-container__left-Container">
                                    <Label text="Choose where you want to add the new indicator and fill the form on the right"></Label>
                                    <SingleSelectMasterData
                                        hideSelectionOnCategory={true}
                                        hierarchicalData={hierarchicalData}
                                        hideChildIndicatorsOnly={true}
                                        updateSelectedRadioButton={handleRadioButtonChange}
                                        toggleExpansion={handleExpansionToggle}
                                        preventSelectionOfSubCategoriesHavingSubCategories={true}
                                        fetchNextHierachySet={fetchNextHierachySet}
                                        radioListClassName={'radio-list-wrapper'}
                                    />
                                    <div className="create-new-option-container">
                                        <div className="create-new-category-checkbox-label">
                                            <RadioButton
                                                id={'createNewSubCategory1'}
                                                checked={
                                                    createNewEntityType ===
                                                    NestCreationTypes.CATEGORY
                                                }
                                                name={'createNewSubCategory'}
                                                value={NestCreationTypes.CATEGORY}
                                                onChange={(event: any) => {
                                                    onClickOfRadioButton(event.target.value);
                                                }}
                                            />
                                            <Label
                                                style={{ paddingTop: '0rem' }}
                                                text="Category/Sub category not found. Create new"></Label>
                                        </div>
                                        <div className="create-new-category-checkbox-label">
                                            <RadioButton
                                                id={'createNewSubCategory2'}
                                                checked={
                                                    createNewEntityType ===
                                                    NestCreationTypes.SUB_CATEGORY
                                                }
                                                name={'createNewSubCategory'}
                                                value={NestCreationTypes.SUB_CATEGORY}
                                                onChange={(event: any) => {
                                                    onClickOfRadioButton(event.target.value);
                                                }}
                                            />
                                            <Label
                                                style={{ paddingTop: '0rem' }}
                                                text="Sub category not found. Create new"></Label>
                                        </div>
                                    </div>
                                </div>
                                <div className="indicator-copy-container__right-Container">
                                    <Label text="Fill in the below form:"></Label>
                                    <div className="indicator-copy-container__right-Container__input-type-container">
                                        <TabNavigation
                                            tabs={INDICATOR_TAB_VALUES}
                                            className="tab-navigation"
                                            onTabClick={handleTabClick}
                                        />
                                        <div className="content-area">
                                            {tab === INDICATOR_TAB_VALUES[0] &&
                                                indicator?.status === ApiStatus.SUCCESS && (
                                                    <IndicatorDetails
                                                        data={indicatorModel}
                                                        fieldErrors={fieldErrors}
                                                        sourceList={sourceList?.data || []}
                                                        dataCount={dataCount}
                                                        isOverride={isOverride}
                                                        isShowUpdateTypeAlert={
                                                            isShowUpdateTypeAlert
                                                        }
                                                        sourceSearchValue={sourceSearchValue}
                                                        isSetUpIndicatorSourceDisabled={
                                                            isSetUpDisabled
                                                        }
                                                        setOverride={setOverride}
                                                        setShowUpdateTypeAlert={
                                                            setShowUpdateTypeAlert
                                                        }
                                                        onChange={handleChange}
                                                        onSourceSearchChange={
                                                            handleSourceSearchChange
                                                        }
                                                        onSourceSelection={handleSourceSelection}
                                                        updateFieldErrors={updateFieldErrors}
                                                    />
                                                )}
                                            {tab === INDICATOR_TAB_VALUES[1] && (
                                                <IndicatorCountryList
                                                    data={indicatorCountryData || []}
                                                    fieldErrors={fieldErrors}
                                                    onChange={handleIndicatorValueChange}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : wizardScreen == 1 ? (
                        <div className="form-container">
                            <CategoryForm
                                fieldErrors={fieldErrors}
                                name={name}
                                handleNameChange={handleCategoryNameChange}
                                sortOrder={sortOrder}
                                handleSortOrderChange={handleCategorySortOrderChange}
                                description={description}
                                handleDescription={handleCategoryDescription}
                            />
                        </div>
                    ) : wizardScreen === 2 &&
                      createNewEntityType === NestCreationTypes.SUB_CATEGORY ? (
                        <div className="add-sub-category-container">
                            <div className="add-sub-category-container__left-Container">
                                <Label text={ADD_NEW_SUB_CATEGORY_BANNER_MESSAGE}></Label>
                                <SingleSelectMasterData
                                    hideSelectionOnCategory={false}
                                    hierarchicalData={hierarchicalData}
                                    updateSelectedRadioButton={handleRadioClicked}
                                    toggleExpansion={handleExpansionToggle}
                                    hideChildIndicatorsOnly={true}
                                    preventSelectionOfSubCategoriesHavingIndicators={true}
                                    fetchNextHierachySet={fetchNextHierachySet}
                                />
                            </div>
                            <div className="add-sub-category-container__right-Container">
                                <Label text="Fill in the below form"></Label>
                                <div className="subcategory-form">
                                    <FormField className="subcategory-form-field">
                                        <Label text="Subcategory name"></Label>
                                        <Input
                                            type={InputType.TEXT}
                                            value={subCatName}
                                            onChange={handleSubCatNameChange}
                                            onBlur={handleSubCatNameChange}
                                        />
                                        <FieldErrorList
                                            field={SubCategoryField.Name}
                                            errors={fieldErrors}
                                        />
                                    </FormField>
                                    <FormField className="subcategory-form-field">
                                        <Label text="Sort Order (required)"></Label>
                                        <Input
                                            type={InputType.TEXT}
                                            value={subCatSortOrder}
                                            onChange={(event) =>
                                                handleSubCatSortOrderChange(event.target.value)
                                            }
                                            onBlur={(event) =>
                                                handleSubCatSortOrderChange(event.target.value)
                                            }
                                        />
                                        <FieldErrorList
                                            field={SubCategoryField.SortOrder}
                                            errors={fieldErrors}
                                        />
                                    </FormField>
                                    <FormField className="subcategory-form-field">
                                        <Label text="Description"></Label>
                                        <TextArea
                                            value={subCatDescription}
                                            onChange={handleSubCatDescription}
                                            onBlur={handleSubCatDescription}
                                        />
                                        <FieldErrorList
                                            field={SubCategoryField.Description}
                                            errors={fieldErrors}
                                            data-testid="error"
                                        />
                                    </FormField>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="form-container">
                            <SubcategoryForm
                                fieldErrors={fieldErrors}
                                name={subCatName}
                                handleNameChange={handleSubCatNameChange}
                                sortOrder={subCatSortOrder}
                                handleSortOrderChange={handleSubCatSortOrderChange}
                                description={subCatDescription}
                                handleDescription={handleSubCatDescription}
                            />
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className="indicator-copy-footer">
                    <div className="footer-container">
                        <div className="footer-container__left">
                            <Button
                                size="medium"
                                type="button"
                                disabled={primaryButtonDisable}
                                onClick={validateCopyForm}>
                                {(wizardScreen == 0 &&
                                    (createNewEntityType == NestCreationTypes.SUB_CATEGORY ||
                                        createNewEntityType == NestCreationTypes.CATEGORY)) ||
                                wizardScreen == 1
                                    ? ACTION_ITEMS.NEXT_BUTTON
                                    : wizardScreen == 0
                                    ? ACTION_ITEMS.SUBMIT_BUTTON
                                    : ACTION_ITEMS.SUBMIT_BUTTON}
                            </Button>
                            <Button
                                size="medium"
                                variant="secondary"
                                type="button"
                                onClick={() => {
                                    handleClose();
                                    dispatch(masterDataActions.setFieldErrors([]));
                                }}>
                                {ACTION_ITEMS.CANCEL_BUTTON}
                            </Button>
                        </div>
                        <div className="footer-container__right">
                            {wizardScreen == 1 || wizardScreen == 2 ? (
                                <Button
                                    variant={ButtonVariant.Secondary}
                                    onClick={() => {
                                        onBackButtonClicked();
                                        dispatch(masterDataActions.setFieldErrors([]));
                                    }}>
                                    {ACTION_ITEMS.BACK_BUTTON}
                                </Button>
                            ) : (
                                ''
                            )}
                        </div>
                    </div>
                </ModalFooter>
            </Modal>
            {isConfirmationVisible && (
                <MessageModal
                    headerName={COPY_CONFIRMATION_TITLE}
                    message={COPY_CONFIRMATION_DESCRIPTION}
                    isVisible={isConfirmationVisible}
                    isSecondaryButtonVisible={true}
                    primaryButton={ButtonLabel.Ok}
                    secondaryButton={ButtonLabel.Cancel}
                    onPrimaryAction={handleConfirmation}
                    onSecondaryAction={handleConfirmationClose}
                />
            )}
            <MessageModal
                headerName={validationPopupDetails.title}
                message={validationPopupDetails.text}
                isVisible={isValidationPopupVisible}
                isSecondaryButtonVisible={false}
                type={MessageType.Error}
                primaryButton={ButtonLabel.Ok}
                secondaryButton={ButtonLabel.Cancel}
                onPrimaryAction={() => setIsValidationPopupVisible(false)}
                onSecondaryAction={() => setIsValidationPopupVisible(false)}
            />
        </>
    );
}
